/**
 * 左侧项目管理面板
 * 支持创建项目、切换项目、查看项目信息
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ProjectInfo, EntityType } from '../types';
import { projectApi, getErrorMessage, shareApi } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import ProjectEditDialog from './ProjectEditDialog';
import ShareDialog from './ShareDialog';
import {
  FolderPlusIcon,
  FolderIcon,
  TrashIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  PencilSquareIcon,
  DocumentTextIcon,
  UserCircleIcon,
  BookOpenIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ShareIcon,
  ArrowPathIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

interface ProjectPanelProps {
  currentProject: ProjectInfo | null;
  onProjectSelect: (project: ProjectInfo) => void;
  onProjectCreate: (project: ProjectInfo) => void;
  onProjectDelete: () => void;
  refreshTrigger?: number;
  /** 底层文档类型：bid=投标文件（默认）/ tender=招标文件 / project_app=项目申报 / paper=论文 / solution=技术方案 */
  docKind?: string;
}

const ProjectPanel: React.FC<ProjectPanelProps> = ({
  currentProject,
  onProjectSelect,
  onProjectCreate,
  onProjectDelete,
  refreshTrigger,
  docKind,
}) => {
  const { user, isAdmin } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [panelWidth, setPanelWidth] = useState(320);
  const isResizingRef = useRef(false);
  const resizeStartXRef = useRef(0);
  const resizeStartWidthRef = useRef(0);
  const [projects, setProjects] = useState<ProjectInfo[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newDescriptiveLevel, setNewDescriptiveLevel] = useState('detailed');
  const [creating, setCreating] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [editingProject, setEditingProject] = useState<ProjectInfo | null>(null);
  const [projectSource, setProjectSource] = useState<'owned' | 'managed' | 'shared'>('owned');
  const [shareTarget, setShareTarget] = useState<{ type: EntityType; id: number; name: string; isOwner: boolean } | null>(null);
  const [transferTarget, setTransferTarget] = useState<{ type: EntityType; id: number; name: string } | null>(null);
  const [transferUsername, setTransferUsername] = useState('');
  const [transferCancelShares, setTransferCancelShares] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [transferChecking, setTransferChecking] = useState(false);
  const [transferCheckResult, setTransferCheckResult] = useState<{ exists: boolean; display_name?: string } | null>(null);

  const loadProjects = useCallback(async () => {
    try {
      const response = await projectApi.list(
        projectSource === 'owned' ? undefined : projectSource,
        docKind
      );
      setProjects(response.data.projects);
    } catch {
      // 静默失败
    }
  }, [projectSource]);

  useEffect(() => {
    void loadProjects();
  }, [loadProjects]);

  // 始终调用最新的 onProjectSelect，避免闭包陈旧
  const onProjectSelectRef = useRef(onProjectSelect);
  useEffect(() => {
    onProjectSelectRef.current = onProjectSelect;
  }, [onProjectSelect]);

  // 切换模块后（首次加载列表且无当前项目）自动定位到第一个项目
  const autoSelectedRef = useRef(false);
  useEffect(() => {
    if (autoSelectedRef.current) return;
    if (projects.length > 0 && !currentProject) {
      autoSelectedRef.current = true;
      onProjectSelectRef.current(projects[0]);
    }
  }, [projects, currentProject]);

  // 当 refreshTrigger 变化时：本地即刻更新当前项目的状态，同时从 API 重载列表
  useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger > 0) {
      // 本地即刻更新列表中当前项目的状态（无需等 API 返回）
      if (currentProject) {
        setProjects(prev =>
          prev.map(p =>
            p.id === currentProject.id
              ? { ...p, status: currentProject.status }
              : p
          )
        );
      }
      // 同时从 API 重新加载，确保其他项目状态也同步
      void loadProjects();
    }
  }, [refreshTrigger, loadProjects, currentProject]);

  // 面板拖拽调整宽度
  const handleResizeMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    isResizingRef.current = true;
    resizeStartXRef.current = e.clientX;
    resizeStartWidthRef.current = panelWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [panelWidth]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizingRef.current) return;
      const delta = e.clientX - resizeStartXRef.current;
      const newWidth = Math.max(240, Math.min(480, resizeStartWidthRef.current + delta));
      setPanelWidth(newWidth);
    };
    const handleMouseUp = () => {
      if (isResizingRef.current) {
        isResizingRef.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      setCreating(true);
      setMessage(null);
      const response = await projectApi.create({
        name: newName.trim(),
        description: newDesc.trim() || undefined,
        outline_descriptive_level: newDescriptiveLevel,
        doc_kind: docKind || 'bid',
      });
      onProjectCreate(response.data);
      setShowCreate(false);
      setNewName('');
      setNewDesc('');
      setNewDescriptiveLevel('detailed');
      await loadProjects();
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error, '创建项目失败') });
    } finally {
      setCreating(false);
    }
  };

  const handleSelect = (project: ProjectInfo) => {
    if (currentProject?.id === project.id) return;
    onProjectSelect(project);
  };

  const handleDelete = async (project: ProjectInfo, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(`确定要将项目「${project.name}」移至回收站吗？\n\n可在"我的设置 → 回收站"中恢复或彻底删除。`)) return;
    try {
      await projectApi.delete(project.id);
      if (currentProject?.id === project.id) {
        onProjectDelete();
      }
      await loadProjects();
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error, '删除项目失败') });
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const closeTransferDialog = () => {
    setTransferTarget(null);
    setTransferUsername('');
    setTransferCancelShares(false);
    setTransferCheckResult(null);
  };

  const handleCheckUser = async () => {
    const username = transferUsername.trim();
    if (!username) return;
    setTransferChecking(true);
    setTransferCheckResult(null);
    try {
      const res = await shareApi.checkUser(username);
      setTransferCheckResult(res.data);
    } catch {
      setTransferCheckResult({ exists: false });
    } finally {
      setTransferChecking(false);
    }
  };

  const handleTransfer = async () => {
    if (!transferTarget || !transferUsername.trim()) return;
    if (!transferCheckResult?.exists) {
      setMessage({ type: 'error', text: '请先检查目标用户名是否存在' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }
    try {
      setTransferring(true);
      const res = await shareApi.transferOwnership({
        entity_type: transferTarget.type,
        entity_id: transferTarget.id,
        target_username: transferUsername.trim(),
        cancel_shares: transferCancelShares,
      });
      setTransferTarget(null);
      setTransferUsername('');
      setTransferCancelShares(false);
      setTransferCheckResult(null);
      await loadProjects();
      setMessage({ type: 'success', text: res.data.message });
      setTimeout(() => setMessage(null), 4000);
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error, '转移失败') });
      setTimeout(() => setMessage(null), 4000);
    } finally {
      setTransferring(false);
    }
  };

  const openEditDialog = (project: ProjectInfo, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingProject(project);
  };

  const handleEditSaved = async (updated: ProjectInfo) => {
    if (currentProject?.id === updated.id) {
      onProjectSelect(updated);
    }
    await loadProjects();
  };

  const getStatusBadge = (status: string) => {
    const map: Record<string, { label: string; cls: string }> = {
      draft: { label: '草稿', cls: 'bg-gray-100 text-gray-600' },
      parsing: { label: '解析中', cls: 'bg-yellow-100 text-yellow-700' },
      editing: { label: '编辑中', cls: 'bg-blue-100 text-blue-700' },
      completed: { label: '已完成', cls: 'bg-green-100 text-green-700' },
    };
    const info = map[status] || { label: status, cls: 'bg-gray-100 text-gray-600' };
    return (
      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${info.cls}`}>
        {info.label}
      </span>
    );
  };

  // 收起状态：只显示一个窄条 + 展开按钮
  if (collapsed) {
    return (
      <div className="bg-white shadow-sm border-r border-gray-200 flex flex-col items-center h-full py-4" style={{ width: 44 }}>
        <button
          onClick={() => setCollapsed(false)}
          className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
          title="展开面板"
        >
          <ChevronDoubleRightIcon className="w-5 h-5" />
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-sm border-r border-gray-200 flex flex-col h-full relative" style={{ width: panelWidth }}>
      {/* 标题区 */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-bold text-gray-900">
            {docKind === 'project_app' ? '项目申报书编写'
              : docKind === 'paper' ? '论文编写'
              : docKind === 'solution' ? '方案编写'
              : '投标文件编写助手'}
          </h1>
          <div className="flex items-center space-x-1">
            <button
              onClick={() => setShowCreate(!showCreate)}
              className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors"
              title="新建项目"
            >
              <FolderPlusIcon className="w-3.5 h-3.5 mr-1" />
              新建
            </button>
            <button
              onClick={() => setCollapsed(true)}
              className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
              title="收起面板"
            >
              <ChevronDoubleLeftIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* 创建项目表单 */}
      {showCreate && (
        <div className="p-4 border-b border-gray-200 bg-blue-50/50">
          <div className="space-y-2">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="项目名称"
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-1.5 border"
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            />
            <input
              type="text"
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
              placeholder="项目描述（可选）"
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-1.5 border"
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            />
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                提纲描述详细度
              </label>
              <select
                value={newDescriptiveLevel}
                onChange={(e) => setNewDescriptiveLevel(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-1.5 border bg-white"
              >
                <option value="simple">简单 — 1-2句话概括</option>
                <option value="detailed">详细 — 2-4句话说明（推荐）</option>
                <option value="very_detailed">非常详细 — 4-6句话展开</option>
              </select>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={handleCreate}
                disabled={creating || !newName.trim()}
                className="flex-1 inline-flex justify-center items-center px-3 py-1.5 text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {creating ? '创建中...' : '确认创建'}
              </button>
              <button
                onClick={() => setShowCreate(false)}
                className="px-3 py-1.5 text-xs font-medium rounded-md text-gray-600 bg-white border border-gray-300 hover:bg-gray-50 transition-colors"
              >
                取消
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 消息提示 */}
      {message && (
        <div className={`mx-4 mt-3 rounded-md px-3 py-2 text-xs ${
          message.type === 'success'
            ? 'bg-green-50 text-green-700 border border-green-200'
            : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {message.text}
        </div>
      )}

      {/* 当前项目信息 */}
      {currentProject && (
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-2 mb-1">
            <FolderIcon className="w-4 h-4 text-blue-600 flex-shrink-0" />
            <span className="text-sm font-semibold text-gray-900 truncate">{currentProject.name}</span>
          </div>
          {currentProject.description && (
            <p className="text-xs text-gray-500 mt-1 ml-6">{currentProject.description}</p>
          )}
          {/* 只读提示横幅 */}
          {currentProject.source === 'shared' && currentProject.share_permission === 'read' && (
            <div className="mt-2 ml-6 flex items-center gap-1.5 px-2 py-1 rounded text-xs bg-amber-50 border border-amber-200 text-amber-700">
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m0-10v4m0 0a2 2 0 100-4 2 2 0 000 4z" />
              </svg>
              此项目为只读共享，无法进行编辑操作
            </div>
          )}
          <div className="flex items-center space-x-3 mt-2 ml-6 text-xs text-gray-500 flex-wrap gap-y-1">
            <span className="flex items-center">
              <DocumentTextIcon className="w-3 h-3 mr-0.5" />
              {currentProject.file_count} 文件
            </span>
            <span>{currentProject.outline_count} 章节</span>
            <span>{currentProject.content_count} 内容</span>
            {getStatusBadge(currentProject.status)}
            {currentProject.knowledge_base_names && currentProject.knowledge_base_names.length > 0 && (
              <>
                {currentProject.knowledge_base_names.map((name, idx) => (
                  <span key={idx} className="flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-purple-50 text-purple-600">
                    <BookOpenIcon className="w-3 h-3" />
                    {name}
                  </span>
                ))}
              </>
            )}
            {isAdmin && currentProject.owner_name && (
              <span className="flex items-center gap-0.5 text-gray-400">
                <UserCircleIcon className="w-3 h-3" />
                {currentProject.owner_name}
              </span>
            )}
          </div>
        </div>
      )}

      {/* 来源 Tab：我的 / 管理 / 共享 */}
      <div className="px-4 pt-1 pb-0 border-b border-gray-100">
        <div className="flex space-x-1">
          <button
            onClick={() => setProjectSource('owned')}
            className={`px-3 py-1.5 text-xs font-medium rounded-t-md transition-colors ${
              projectSource === 'owned'
                ? 'text-blue-600 bg-blue-50 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            我的
          </button>
          {isAdmin && (
            <button
              onClick={() => setProjectSource('managed')}
              className={`px-3 py-1.5 text-xs font-medium rounded-t-md transition-colors ${
                projectSource === 'managed'
                  ? 'text-blue-600 bg-blue-50 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              管理
            </button>
          )}
          <button
            onClick={() => setProjectSource('shared')}
            className={`px-3 py-1.5 text-xs font-medium rounded-t-md transition-colors ${
              projectSource === 'shared'
                ? 'text-blue-600 bg-blue-50 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            共享
          </button>
        </div>
      </div>

      {/* 项目列表 */}
      <div className="flex-1 overflow-y-auto p-2">
        <div className="text-xs font-medium text-gray-500 uppercase tracking-wider px-2 py-1 mb-1">
          {projectSource === 'owned' ? '我的项目' : projectSource === 'managed' ? '所有项目' : '共享项目'} ({projects.length})
        </div>
        {projects.length === 0 ? (
          <div className="text-center py-8 text-sm text-gray-400">
            <FolderIcon className="mx-auto h-8 w-8 mb-2" />
            {projectSource === 'managed' ? '暂无其他用户的项目' : '暂无项目，点击"新建"创建'}
          </div>
        ) : (
          <div className="space-y-1">
            {projects.map((project) => (
              <div key={project.id}>
                <div
                  onClick={() => handleSelect(project)}
                  className={`group flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer transition-colors relative ${
                    currentProject?.id === project.id
                      ? 'bg-blue-50 border border-blue-200'
                      : 'hover:bg-gray-50 border border-transparent'
                  }`}
                >
                  <div className="flex-1 min-w-0 pr-1">
                    <div className="flex items-center space-x-1.5">
                      {currentProject?.id === project.id && (
                        <CheckCircleIcon className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                      )}
                      <span className={`text-sm truncate ${currentProject?.id === project.id ? 'font-semibold text-blue-700' : 'text-gray-700'}`}>
                        {project.name}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2 mt-0.5 ml-5">
                      {getStatusBadge(project.status)}
                      <span className="text-xs text-gray-400">
                        {project.file_count > 0 && `${project.file_count}文件 `}
                        {project.outline_count > 0 && `${project.outline_count}章`}
                      </span>
                      {isAdmin && project.owner_name && (
                        <span className="text-xs text-gray-400 flex items-center gap-0.5">
                          <UserCircleIcon className="w-3 h-3" />
                          {project.owner_name}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center space-x-0.5 opacity-0 group-hover:opacity-100 transition-all bg-white rounded-lg px-1 py-0.5 shadow-sm">
                    {project.user_id === user?.id && (
                      <>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setTransferUsername('');
                            setTransferCancelShares(false);
                            setTransferCheckResult(null);
                            setTransferTarget({ type: 'project', id: project.id, name: project.name });
                          }}
                          className="p-1 rounded text-gray-400 hover:text-orange-500 hover:bg-orange-50 transition-all"
                          title="转移所有权"
                        >
                          <ArrowPathIcon className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShareTarget({ type: 'project', id: project.id, name: project.name, isOwner: true });
                          }}
                          className="p-1 rounded text-gray-400 hover:text-purple-500 hover:bg-purple-50 transition-all"
                          title="分享"
                        >
                          <ShareIcon className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                    {/* 共享项目且只读权限：禁用编辑按钮 */}
                    {project.user_id === user?.id || project.share_permission === 'edit' ? (
                      <button
                        onClick={(e) => openEditDialog(project, e)}
                        className="p-1 rounded text-gray-400 hover:text-blue-500 hover:bg-blue-50 transition-all"
                        title="修改项目"
                      >
                        <PencilSquareIcon className="w-3.5 h-3.5" />
                      </button>
                    ) : (
                      <span className="p-1 rounded text-gray-300 cursor-not-allowed" title="只读项目，无法编辑">
                        <PencilSquareIcon className="w-3.5 h-3.5" />
                      </span>
                    )}
                    {/* 共享项目：禁用删除按钮 */}
                    {project.user_id === user?.id ? (
                      <button
                        onClick={(e) => handleDelete(project, e)}
                        className="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all"
                        title="删除项目"
                      >
                        <TrashIcon className="w-3.5 h-3.5" />
                      </button>
                    ) : (
                      <span className="p-1 rounded text-gray-300 cursor-not-allowed" title="共享项目，无法删除">
                        <TrashIcon className="w-3.5 h-3.5" />
                      </span>
                    )}
                  </div>
                  {project.share_permission && (
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                      project.share_permission === 'edit' ? 'bg-orange-100 text-orange-600' : 'bg-blue-100 text-blue-600'
                    }`}>
                      {project.share_permission === 'edit' ? '可编辑' : '只读'}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 底部链接 */}
      <div className="border-t border-gray-200 p-3">
        <div className="flex items-center justify-end">
          <div className="flex items-center space-x-4">
          <a
            href="http://www.jiudingmap.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-400 hover:text-gray-600 transition-colors"
            title="GitHub"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.30.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
            </svg>
          </a>
          <a
            href="http://www.fahua.vip"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:opacity-75 transition-opacity"
            title="夺标官网"
          >
            <img
              src="/logo192.png"
              alt="夺标"
              className="w-5 h-5"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          </a>
        </div>
        </div>
      </div>

      {/* 项目编辑弹窗 */}
      {editingProject && (
        <ProjectEditDialog
          project={editingProject}
          isOpen={true}
          onClose={() => setEditingProject(null)}
          onSaved={handleEditSaved}
        />
      )}

      {/* 转移所有权弹窗 */}
      {transferTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={closeTransferDialog} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-sm mx-4">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">转移所有权</h2>
                <p className="text-xs text-gray-500 mt-0.5">项目：{transferTarget.name}</p>
              </div>
              <button
                onClick={closeTransferDialog}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-700">
                转移后该项目将归属目标用户。已有的分享记录默认保留并自动归属新所有者，也可选择取消所有分享。
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">目标用户名</label>
                <div className="flex gap-2">
                  <input
                    type="text" value={transferUsername}
                    onChange={(e) => {
                      setTransferUsername(e.target.value);
                      setTransferCheckResult(null);
                    }}
                    placeholder="输入要转移给的用户的用户名"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <button
                    onClick={handleCheckUser}
                    disabled={transferChecking || !transferUsername.trim()}
                    className="inline-flex items-center gap-1 px-3 py-2 text-xs font-medium rounded-lg border transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100"
                  >
                    {transferChecking ? (
                      <svg className="w-3.5 h-3.5 animate-spin" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                    ) : (
                      <CheckCircleIcon className="w-3.5 h-3.5" />
                    )}
                    检查
                  </button>
                </div>
                {transferCheckResult?.exists && (
                  <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                    <CheckCircleIcon className="w-3.5 h-3.5" />
                    用户存在：{transferCheckResult.display_name || transferUsername.trim()}
                  </p>
                )}
                {transferCheckResult && !transferCheckResult.exists && (
                  <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                    <ExclamationCircleIcon className="w-3.5 h-3.5" />
                    用户不存在
                  </p>
                )}
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={transferCancelShares}
                  onChange={(e) => setTransferCancelShares(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                <span className="text-sm text-gray-600">同时取消所有已分享记录</span>
              </label>
            </div>
            <div className="flex items-center justify-end space-x-3 px-5 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <button onClick={closeTransferDialog}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors">
                取消
              </button>
              <button onClick={handleTransfer} disabled={transferring || !transferCheckResult?.exists}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                <ArrowPathIcon className="w-4 h-4 mr-1" />
                {transferring ? '转移中...' : '确认转移'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 共享弹窗 */}
      {shareTarget && (
        <ShareDialog
          isOpen={true}
          onClose={() => setShareTarget(null)}
          entityType={shareTarget.type}
          entityId={shareTarget.id}
          entityName={shareTarget.name}
          isOwner={shareTarget.isOwner}
        />
      )}

      {/* 面板拖拽调整宽度手柄 */}
      <div
        onMouseDown={handleResizeMouseDown}
        className="absolute top-0 -right-1 w-2 h-full cursor-col-resize hover:bg-blue-400/30 active:bg-blue-500/40 rounded transition-colors z-10"
        title="拖动调整面板宽度"
      />
    </div>
  );
};

export default ProjectPanel;
